import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Pencil, Plus, Trash2 } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { api, type Asset } from '../api/client';
import { useSession } from '../app/session';
import { ImagePicker } from '../pages/LiveTvPage';
import { Empty, ErrorBox, Field, Loading, Modal, Toggle } from './ui';
import { Select } from './Select';
import { ask } from './Confirm';

/** Icon keys shipped in the TV app (must match the Android icon set). */
export const TV_ICONS = [
  'tv', 'list', 'film', 'music', 'bell', 'dining', 'info', 'apps', 'alarm', 'star', 'spa', 'map',
  'weather', 'globe', 'cast', 'settings', 'pool', 'fitness', 'beach', 'meeting', 'kids', 'shuttle',
  'bed', 'location', 'phone', 'gallery', 'currency', 'news', 'calendar', 'cocktail', 'coffee', 'youtube', 'netflix',
];

export type FieldSpec =
  | { key: string; label: string; type: 'text' | 'textarea' | 'url' | 'color'; placeholder?: string; hint?: string; required?: boolean }
  | { key: string; label: string; type: 'number'; hint?: string }
  | { key: string; label: string; type: 'toggle'; hint?: string }
  | { key: string; label: string; type: 'icon' }
  | { key: string; label: string; type: 'select'; options: { value: string; label: string }[]; hint?: string }
  | { key: string; label: string; type: 'image'; urlKey: string; hint?: string }
  | { key: string; label: string; type: 'tags'; hint?: string; placeholder?: string }
  | { key: string; label: string; type: 'custom'; render: (value: Record<string, unknown>, set: (patch: Record<string, unknown>) => void) => ReactNode };

type Values = Record<string, unknown>;

/** Generic modal form driven by a field list. Image fields keep `<key>` (asset id) and `<urlKey>` (preview). */
export function EntityForm({
  title,
  fields,
  initial,
  onSave,
  onClose,
  wide,
}: {
  title: string;
  fields: FieldSpec[];
  initial: Values;
  onSave: (values: Values) => Promise<unknown>;
  onClose: () => void;
  wide?: boolean;
}) {
  const [values, setValues] = useState<Values>(initial);
  const set = (patch: Values) => setValues((v) => ({ ...v, ...patch }));
  const save = useMutation({ mutationFn: () => onSave(values), onSuccess: onClose });
  const missing = fields.some((f) => 'required' in f && f.required && !values[f.key]);

  return (
    <Modal
      title={title}
      onClose={onClose}
      wide={wide}
      footer={
        <button className="btn primary" disabled={missing || save.isPending} onClick={() => save.mutate()}>
          Save
        </button>
      }
    >
      {fields.map((f) => {
        const v = values[f.key];
        switch (f.type) {
          case 'text':
          case 'url':
            return (
              <Field key={f.key} label={f.label} hint={f.hint}>
                <input type={f.type === 'url' ? 'url' : 'text'} value={(v as string) ?? ''} placeholder={f.placeholder} onChange={(e) => set({ [f.key]: e.target.value })} />
              </Field>
            );
          case 'color':
            return (
              <Field key={f.key} label={f.label} hint={f.hint}>
                <div className="row">
                  <input type="color" value={(v as string) || '#6E4BFF'} onChange={(e) => set({ [f.key]: e.target.value.toUpperCase() })} />
                  <span className="mono">{(v as string) || '—'}</span>
                </div>
              </Field>
            );
          case 'textarea':
            return (
              <Field key={f.key} label={f.label} hint={f.hint}>
                <textarea value={(v as string) ?? ''} placeholder={f.placeholder} onChange={(e) => set({ [f.key]: e.target.value })} style={{ minHeight: 100 }} />
              </Field>
            );
          case 'number':
            return (
              <Field key={f.key} label={f.label} hint={f.hint}>
                <input type="number" value={(v as number | null) ?? ''} onChange={(e) => set({ [f.key]: e.target.value === '' ? null : Number(e.target.value) })} />
              </Field>
            );
          case 'toggle':
            return (
              <Field key={f.key} label={f.label} hint={f.hint}>
                <Toggle on={!!v} onChange={(on) => set({ [f.key]: on })} />
              </Field>
            );
          case 'icon':
            return (
              <Field key={f.key} label={f.label}>
                <Select value={(v as string) ?? 'star'} onChange={(e) => set({ [f.key]: e.target.value })}>
                  {TV_ICONS.map((i) => (
                    <option key={i}>{i}</option>
                  ))}
                </Select>
              </Field>
            );
          case 'select':
            return (
              <Field key={f.key} label={f.label} hint={f.hint}>
                <Select value={(v as string) ?? ''} onChange={(e) => set({ [f.key]: e.target.value })}>
                  {f.options.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              </Field>
            );
          case 'image':
            return (
              <ImagePicker
                key={f.key}
                label={f.label}
                hint={f.hint}
                value={(values[f.urlKey] as string) ?? null}
                onChange={(a: Asset | null) => set({ [f.key]: a?.id ?? null, [f.urlKey]: a?.url ?? null })}
              />
            );
          case 'tags':
            return (
              <Field key={f.key} label={f.label} hint={f.hint ?? 'Comma-separated'}>
                <input
                  value={((v as string[]) ?? []).join(', ')}
                  placeholder={f.placeholder}
                  onChange={(e) => set({ [f.key]: e.target.value.split(',').map((x) => x.trim()).filter(Boolean) })}
                />
              </Field>
            );
          case 'custom':
            return <div key={f.key}>{f.render(values, set)}</div>;
        }
      })}
      <ErrorBox error={save.error} />
    </Modal>
  );
}

/** Strips preview-only keys (…Url) and turns empty strings into null before sending. */
export function cleanValues(values: Values, fields: FieldSpec[]): Values {
  const out: Values = {};
  for (const f of fields) {
    const v = values[f.key];
    out[f.key] = typeof v === 'string' && v.trim() === '' ? null : v;
  }
  return out;
}

/**
 * Ordered list of site content (facilities, venues, topics, apps) with enable toggle,
 * reorder arrows, edit and delete. Saving anything republishes the TVs.
 */
export function SiteContentList<T extends { id: string; enabled: boolean }>({
  siteId,
  list,
  itemPath,
  title,
  fields,
  toForm,
  newDefaults,
  render,
  extraActions,
  updateMethod = 'patch',
}: {
  siteId: string;
  list: 'facilities' | 'venues' | 'info-topics' | 'apps';
  itemPath: string;
  title: string;
  fields: FieldSpec[];
  toForm: (item: T) => Values;
  newDefaults: Values;
  render: (item: T) => ReactNode;
  extraActions?: (item: T) => ReactNode;
  updateMethod?: 'patch' | 'put';
}) {
  const { can } = useSession();
  const qc = useQueryClient();
  const listPath = list === 'info-topics' ? `/sites/${siteId}/info` : `/sites/${siteId}/${list}`;
  const key = [list, siteId];
  const query = useQuery({
    queryKey: key,
    queryFn: async () => {
      const res = await api.get<{ items?: T[]; topics?: T[] }>(listPath);
      return (res.items ?? res.topics ?? []) as T[];
    },
  });
  const [edit, setEdit] = useState<T | 'new' | null>(null);
  const invalidate = () => qc.invalidateQueries({ queryKey: key });
  const reorder = useMutation({ mutationFn: (ids: string[]) => api.put(`/sites/${siteId}/${list}/order`, { ids }), onSuccess: invalidate });
  const toggle = useMutation({
    mutationFn: (item: T) =>
      updateMethod === 'patch'
        ? api.patch(`/${itemPath}/${item.id}`, { enabled: !item.enabled })
        : api.put(`/${itemPath}/${item.id}`, { ...cleanValues(toForm(item), fields), enabled: !item.enabled }),
    onSuccess: invalidate,
  });
  const remove = useMutation({ mutationFn: (id: string) => api.delete(`/${itemPath}/${id}`), onSuccess: invalidate });
  const items = query.data ?? [];
  const editable = can('ADMIN');
  const move = (i: number, d: -1 | 1) => {
    const ids = items.map((x) => x.id);
    [ids[i], ids[i + d]] = [ids[i + d]!, ids[i]!];
    reorder.mutate(ids);
  };

  if (query.isLoading) return <Loading />;
  return (
    <div className="card">
      <div className="row" style={{ marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>{title}</h2>
        <span className="muted">{items.length}</span>
        {editable && (
          <button className="btn primary" style={{ marginLeft: 'auto' }} onClick={() => setEdit('new')}>
            <Plus size={15} /> Add
          </button>
        )}
      </div>
      {items.length === 0 ? (
        <Empty>Nothing here yet. Add the first item; it appears on the TVs after saving.</Empty>
      ) : (
        <table>
          <tbody>
            {items.map((item, i) => (
              <tr key={item.id} style={!item.enabled ? { opacity: 0.5 } : undefined}>
                <td style={{ width: 64, whiteSpace: 'nowrap' }}>
                  <button className="icon-btn" disabled={!editable || i === 0} onClick={() => move(i, -1)}>
                    <ArrowUp size={14} />
                  </button>
                  <button className="icon-btn" disabled={!editable || i === items.length - 1} onClick={() => move(i, 1)}>
                    <ArrowDown size={14} />
                  </button>
                </td>
                <td>{render(item)}</td>
                <td style={{ width: 60 }}>
                  <Toggle on={item.enabled} onChange={() => editable && toggle.mutate(item)} label="Shown on TV" />
                </td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {extraActions?.(item)}
                  {editable && (
                    <>
                      <button className="icon-btn" onClick={() => setEdit(item)} title="Edit">
                        <Pencil size={15} />
                      </button>
                      <button className="icon-btn" onClick={() => ask('Delete this item?', () => remove.mutate(item.id))} title="Delete">
                        <Trash2 size={15} />
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <ErrorBox error={toggle.error ?? remove.error ?? reorder.error} />
      {edit && (
        <EntityForm
          title={edit === 'new' ? `Add to ${title}` : `Edit`}
          fields={fields}
          initial={edit === 'new' ? newDefaults : toForm(edit)}
          wide
          onClose={() => setEdit(null)}
          onSave={async (values) => {
            const body = cleanValues(values, fields);
            if (edit === 'new') await api.post(list === 'info-topics' ? `/sites/${siteId}/info-topics` : `/sites/${siteId}/${list}`, body);
            else if (updateMethod === 'put') await api.put(`/${itemPath}/${edit.id}`, body);
            else await api.patch(`/${itemPath}/${edit.id}`, body);
            await invalidate();
          }}
        />
      )}
    </div>
  );
}

/** Asset id → URL lookup for edit forms (the list endpoints return ids only). */
export function useAssetUrls() {
  const assets = useQuery({ queryKey: ['assets'], queryFn: () => api.get<{ items: { id: string; url: string }[] }>('/assets') });
  return (id: unknown) => (typeof id === 'string' ? assets.data?.items.find((a) => a.id === id)?.url ?? null : null);
}
